import { Injectable } from '@angular/core';

import { User } from '../../models/user/user.model';
import { UNAVAILABLE_LOCATION_STATUS, resolveDefaultLocation } from '../../shared/navbar/location-utils';

export type LocationScenario = 'favorite-unavailable' | 'all-unavailable';

export const LOCATION_SCENARIO_STORAGE_KEY = 'spotlight-location-scenario';

export const LOCATION_SCENARIO_LABELS: Record<LocationScenario, string> = {
  'favorite-unavailable': 'Favorite location unavailable',
  'all-unavailable': 'All locations unavailable',
};

// Same hosts Spotlight Next Gen excludes in canUseTokenSimulator.
const PROD_HOSTS = new Set(['spotlight.cfahome.com', 'v2.spotlight.cfahome.com']);

/**
 * Admin-only, non-production simulator for unavailable locations. It never writes to the
 * backend: it rewrites `/api/user`'s locations in the browser after they load, and the scenario
 * lives in sessionStorage so a full reload runs guards and user setup exactly as in production.
 */
@Injectable({ providedIn: 'root' })
export class LocationScenarioService {
  private activeScenario: LocationScenario | null = null;

  public canSimulate(isAdmin: boolean, hostname: string = this.hostname()): boolean {
    return isAdmin && !PROD_HOSTS.has(hostname);
  }

  protected hostname(): string {
    return globalThis.location?.hostname ?? '';
  }

  /** The scenario applied to the current page load, or null. */
  public get active(): LocationScenario | null {
    return this.activeScenario;
  }

  public get isSimulating(): boolean {
    return this.activeScenario !== null;
  }

  public getStoredScenario(): LocationScenario | null {
    try {
      const value = globalThis.sessionStorage.getItem(LOCATION_SCENARIO_STORAGE_KEY);
      return value === 'favorite-unavailable' || value === 'all-unavailable' ? value : null;
    } catch {
      return null;
    }
  }

  public setScenario(scenario: LocationScenario): void {
    try {
      globalThis.sessionStorage.setItem(LOCATION_SCENARIO_STORAGE_KEY, scenario);
    } catch {
      // Storage can be unavailable (private mode); the simulation just won't start.
    }
  }

  public clearScenario(): void {
    try {
      globalThis.sessionStorage.removeItem(LOCATION_SCENARIO_STORAGE_KEY);
    } catch {
      // Nothing to clear if storage is unavailable.
    }
  }

  /**
   * Returns the user with the stored scenario applied, or the user unchanged when there is no
   * scenario or the gate (admin, non-production host) is not met.
   */
  public apply(userData: User, favoriteLocations: string[] | undefined): User {
    this.activeScenario = null;
    const scenario = this.getStoredScenario();
    const isAdmin = !!(userData?.adminFull || userData?.adminView);
    if (!scenario || !this.canSimulate(isAdmin) || !userData.locations?.length) {
      return userData;
    }

    const target = scenario === 'all-unavailable'
      ? null
      : resolveDefaultLocation(userData.locations, favoriteLocations)?.locationNumber;
    this.activeScenario = scenario;

    return {
      ...userData,
      locations: userData.locations.map((location) =>
        target === null || location.locationNumber === target
          ? { ...location, locationStatus: UNAVAILABLE_LOCATION_STATUS, locationName: '' }
          : location
      ),
    };
  }
}
